import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { ToastQueue } from '@adobe/react-spectrum';
import {
  ComboBoxItem,
  TreeGridColumn,
  TreeGridRow,
} from "../../../../../components/ui/TreeGridComponent/types";
import {
  TreeGridComponent,
} from "../../../../../components/ui/TreeGridComponent/TreeGridComponent";
import {
  buildTree as buildTreeStructure,
  calculateDepth,
} from "../../../../../components/ui/TreeGridComponent/utils/treeOperations";

import styles from "./invoice_transaction_section.module.css";
import {
  Button,
  Content,
  DatePicker,
  Dialog,
  DialogContainer,
  Divider,
  Flex,
  Heading,
  Item,
  NumberField,
  Picker,
  Radio,
  RadioGroup,
  TabList,
  TabPanels,
  Tabs,
  TextArea,
  TextField,
  View,
} from "@adobe/react-spectrum";
import { parseDate } from "@internationalized/date";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { ListItemRequest } from "../../../../../services/soap/item/requests/ListItemRequest";
import { ListVATRequest } from "../../../../../services/soap/global/requests/ListVATRequest";
import { usePreloadAssets } from "../../../../../hooks/UsePreloadAssets";
import ProductDropDown from "./formComponents/ProductDropDown";
import UserDropDown from "./formComponents/UserDropDown";
import InvoiceVatDropDown, { InvoiceVatOption } from "./formComponents/InvoiceVatDropDown";
import ProductSelectionModal, {
  SelectedProduct,
} from "../../../../../components/ui/ProductSelectionModal";
import { useTranslation } from "react-i18next";
import { getInvoiceDocumentClass } from "../../../../../shared/invoice/invoiceTypeLogic";

interface InvoiceTransactionComponentProps {
  invoice: any;
  isEdit: boolean;
  isLocked?: boolean;
  onInvoiceChange: React.Dispatch<React.SetStateAction<any>>;
}

type Txn = any;
type TransactionModalTab = "basic" | "advanced" | "controlling";

interface ControllingShareRow {
  id: string;
  share: number;
}

interface ShareEditorState {
  mode: "add" | "edit";
  rowId: string;
  value: string;
}

interface AccountOption {
  id: string;
  name: string;
  label: string;
}

interface TransactionModalDraft {
  itemId: string;
  productName?: string;
  itemRefId?: string;
  name: string;
  context: string;
  transactionDate: string;
  vatType: string;
  vatLabel?: string;
  catalogPrice?: number;
  discountPct?: number;
  unitPrice?: number;
  qty?: number;
  vatPct?: number;
  exclVAT?: number;
  description: string;
  transactionAnalysis: string;
  transactionOwnerId: string;
  transactionOwnerName: string;
  shareMode: "quantity" | "unitsShare" | "percentShare" | "value";
  shares: ControllingShareRow[];
}

interface ModalNumericInputs {
  catalogPrice: string;
  discountPct: string;
  unitPrice: string;
  qty: string;
  vatPct: string;
  exclVAT: string;
}

const toNum = (v: any) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v ?? "").trim();
  if (!s) return 0;
  const n = parseFloat(s.replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n: number) => Math.round(n * 100) / 100;
const absNum = (v: any) => Math.abs(toNum(v));
const asArray = <T,>(v: T | T[] | undefined | null): T[] =>
  Array.isArray(v) ? v : v ? [v] : [];

const formatMoney = (amount: any, currency?: string) => {
  const n = round2(toNum(amount));
  const cur = currency ? ` ${currency}` : "";
  return `${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}${cur}`;
};

const calcExcl = (qty: number, unitPrice: number) => round2(qty * unitPrice);
const calcIncl = (excl: number, vatPct: number) => round2(excl * (1 + (vatPct || 0) / 100));

const detectShareModeFromTxnShares = (shares: any[]): TransactionModalDraft["shareMode"] => {
  if (!shares.length) return "unitsShare";
  const hasPercentage = shares.some((share) => String(share?.percentage ?? "").trim() !== "");
  if (hasPercentage) return "percentShare";
  const hasUnits = shares.some(
    (share) => String(share?.shareUnits ?? share?.units ?? "").trim() !== ""
  );
  if (hasUnits) return "unitsShare";
  const hasTotal = shares.some((share) => String(share?.total ?? "").trim() !== "");
  if (hasTotal) return "value";
  return "unitsShare";
};

const sharePercentByMode = (
  share: ControllingShareRow,
  mode: TransactionModalDraft["shareMode"],
  qty: number,
  excl: number
) => {
  const shareValue = toNum(share.share);
  const absExcl = Math.abs(excl);
  switch (mode) {
    case "value":
      return absExcl !== 0 ? round2((shareValue / absExcl) * 100) : 0;
    case "quantity":
    case "unitsShare":
      return qty !== 0 ? round2((shareValue / qty) * 100) : 0;
    case "percentShare":
    default:
      return round2(shareValue);
  }
};

const shareTotalByMode = (
  share: ControllingShareRow,
  mode: TransactionModalDraft["shareMode"],
  qty: number,
  unitPrice: number,
  excl: number
) => {
  const shareValue = toNum(share.share);
  switch (mode) {
    case "value":
      return round2(shareValue);
    case "quantity":
    case "unitsShare":
      return round2(shareValue * unitPrice);
    case "percentShare":
    default:
      return round2((Math.abs(excl) * shareValue) / 100);
  }
};

const shareUnitsByMode = (
  share: ControllingShareRow,
  mode: TransactionModalDraft["shareMode"],
  qty: number,
  unitPrice: number
) => {
  const shareValue = toNum(share.share);
  switch (mode) {
    case "quantity":
    case "unitsShare":
      return round2(shareValue);
    case "value":
      return unitPrice !== 0 ? round2(shareValue / Math.abs(unitPrice)) : 0;
    case "percentShare":
    default:
      return round2((qty * shareValue) / 100);
  }
};

const normalizeSharePercentage = (value: any) => {
  const pct = round2(toNum(value));
  // Some BE payloads return percentage in basis points (10000 => 100%).
  if (pct > 100 && pct <= 10000) {
    return round2(pct / 100);
  }
  return pct;
};

const getDefaultSingleShareValue = (
  mode: TransactionModalDraft["shareMode"],
  qty: number,
  excl: number
) => {
  if (mode === "percentShare") return 100;
  if (mode === "value") return Math.abs(excl);
  return qty !== 0 ? qty : 1;
};

const getVatPctFromItem = (item: any) =>
  toNum(
    item?.vatValue ??
      item?.VATValue ??
      item?.vat ??
      item?.VAT ??
      item?.vatPercent ??
      item?.vat_percentage ??
      0
  );

const getVatLabelFromItem = (item: any) =>
  String(
    item?.vatLabel ??
      item?.VAT ??
      item?.vatName ??
      item?.vatTitle ??
      item?.VATName ??
      item?.name ??
      ""
  ).trim();

const getCurrencyFromItem = (item: any, fallback?: string) =>
{
  const currencyValue =
    item?.currencyCode ||
    item?.currencyName ||
    item?.currency ||
    fallback ||
    "CZK";

  const raw = String(currencyValue ?? "").trim();
  // Keep payload currency as textual code, not internal numeric id.
  if (/^\d+$/.test(raw)) {
    return String(fallback || "CZK");
  }
  return raw;
};

const getDisplayCurrency = (invoiceCurrency: any, txn: any) => {
  const asCode = (v: any) => {
    const raw = String(v ?? "").trim();
    if (!raw) return "";
    return /^\d+$/.test(raw) ? "" : raw;
  };

  return (
    asCode(invoiceCurrency) ||
    asCode(txn?.otherCurrency) ||
    asCode(txn?.bookkeepingCurrency) ||
    String(invoiceCurrency ?? txn?.otherCurrency ?? txn?.bookkeepingCurrency ?? "").trim()
  );
};

const getUnitPriceFromItem = (item: any) =>
  toNum(
    item?.amount ??
      item?.unitPrice ??
      item?.otherUnitPrice ??
      item?.price ??
      item?.catalogPrice ??
      0
  );

const getVatPctFromRowData = (data: any) =>
  toNum(
    data?.vatValue ??
      data?.vatPct ??
      data?.VATValue ??
      data?.vat ??
      data?.VAT ??
      data?.vatPercent ??
      0
  );

const isVisibleLineTransaction = (txn: Txn) => {
  const vatTitle = String(txn?.VATTitle ?? "").toUpperCase();
  const removeFlag = String(txn?.remove ?? "0");
  if (removeFlag === "1") return false;
  // For GetInvoice TreeGrid view, render only real line transactions (VATTitle = N).
  return vatTitle === "N";
};

const getRevertedDisplayAmount = (txn: Txn, share?: any, invoiceMeta?: any) => {
  // Read-mode display rule:
  // - Sales documents: prefer reverted/backend display amount.
  // - Purchase documents: keep backend response sign as-is.
  // Shares may carry opposite controlling sign, so they are fallback only.
  const has = (v: any) => v !== undefined && v !== null && String(v).trim() !== "";
  const docClass = getInvoiceDocumentClass(invoiceMeta);
  const txnReverted = has(txn?.bookkeepingAmountReverted) ? toNum(txn.bookkeepingAmountReverted) : 0;
  const txnBook = has(txn?.bookkeepingAmount) ? toNum(txn.bookkeepingAmount) : 0;
  const txnOrig = has(txn?.otherAmount) ? toNum(txn.otherAmount) : 0;
  const shareTotal = has(share?.total) ? toNum(share.total) : 0;
  const shareBook = has(share?.bookKeepingAmount) ? toNum(share.bookKeepingAmount) : 0;

  if (docClass === "sales") {
    if (txnReverted !== 0) {
      return txnReverted;
    }
    if (shareTotal !== 0) {
      return shareTotal;
    }
    if (shareBook !== 0) {
      return shareBook;
    }
    if (txnBook !== 0) {
      return -txnBook;
    }
    if (txnOrig !== 0) {
      return -txnOrig;
    }
    return 0;
  }

  if (txnBook !== 0) {
    return txnBook;
  }
  if (txnOrig !== 0) {
    return txnOrig;
  }
  if (txnReverted !== 0) {
    return txnReverted;
  }
  if (shareTotal !== 0) {
    return shareTotal;
  }
  if (shareBook !== 0) {
    return shareBook;
  }
  return txnBook || txnOrig || 0;
};

const buildTreeRows = (
  txns: Txn[],
  invoiceCurrency?: string,
  invoiceMeta?: any,
  // editableMode:
  // - false => Read mode (invoice detail preview)
  // - true  => Add/Edit mode (inline editing in AddUpdateInvoice)
  editableMode: boolean = false
): TreeGridRow[] => {
  const allTxns = Array.isArray(txns) ? txns : [];
  const visibleTxns = (Array.isArray(txns) ? txns : []).filter(isVisibleLineTransaction);
  const byId = new Map<string, Txn>();
  const byKey = new Map<string, Txn>();
  const getTxnOwnKey = (txn: Txn) =>
    String(txn?.childKey ?? txn?.parentKey ?? txn?.id ?? "").trim();
  visibleTxns.forEach((t) => byId.set(String(t.id), t));
  visibleTxns.forEach((t) => {
    const ownKey = getTxnOwnKey(t);
    if (ownKey) byKey.set(ownKey, t);
  });
  const taxByParent = new Map<string, Txn>();
  const taxByChildKey = new Map<string, Txn>();
  allTxns.forEach((t) => {
    if (String(t?.VATTitle ?? "").toUpperCase() !== "T") return;
    const parentId = t?.parent != null ? String(t.parent) : "";
    if (parentId) {
      taxByParent.set(parentId, t);
    }
    const childKey = String(t?.childKey ?? "").trim();
    if (childKey) {
      taxByChildKey.set(childKey, t);
    }
  });

  const childrenMap = new Map<string, string[]>();
  visibleTxns.forEach((t) => {
    let pid = t.parent ? String(t.parent) : (t?.parentId ? String(t.parentId) : "");
    if (!pid) {
      const parentKey = String(t?.parentKey ?? "").trim();
      const ownKey = getTxnOwnKey(t);
      if (parentKey && parentKey !== ownKey) {
        const parentTxn = byKey.get(parentKey);
        if (parentTxn?.id != null) {
          pid = String(parentTxn.id);
        }
      }
    }
    if (!pid) return;
    if (!childrenMap.has(pid)) childrenMap.set(pid, []);
    childrenMap.get(pid)!.push(String(t.id));
  });

  const isRoot = (t: Txn) => {
    let pid = t.parent ? String(t.parent) : (t?.parentId ? String(t.parentId) : "");
    if (pid && byId.has(pid)) return false;

    const parentKey = String(t?.parentKey ?? "").trim();
    const ownKey = getTxnOwnKey(t);
    if (parentKey && parentKey !== ownKey && byKey.has(parentKey)) {
      return false;
    }
    return true;
  };

  const sortByOrderIndex = (a: Txn, b: Txn) => toNum(a.orderIndex) - toNum(b.orderIndex);

  const roots = visibleTxns.filter(isRoot).sort(sortByOrderIndex);
  const rows: TreeGridRow[] = [];

  const walk = (txn: Txn, parentId: string | null, depth: number) => {
    const id = String(txn.id);
    const childIds = (childrenMap.get(id) || []).slice();
    childIds.sort((a, b) => sortByOrderIndex(byId.get(a)!, byId.get(b)!));

    const shares = asArray<any>(txn?.SHARES?.SHARE);
    const firstShare = shares[0] || {};
    const qty = toNum(
      txn.itemQuantity ??
      firstShare?.units ??
      firstShare?.shareUnits ??
      0
    );

    // Some legacy invoices use different sign conventions per row.
    // Prefer row-level sign from unit price fields if present; fallback to doc-level sign.
    const revertedLineAmount = getRevertedDisplayAmount(txn, firstShare, invoiceMeta);
    const exclFromAmounts = revertedLineAmount;

    // Unit price must come from per-unit fields first; total amount is only a fallback.
    let unitPrice = toNum(
      txn.bookkeepingUnitPrice ??
      txn.catalogPrice ??
      txn.otherUnitPrice
    );
    const catalogPriceRaw = toNum(txn.catalogPrice ?? txn.bookkeepingUnitPrice ?? txn.otherUnitPrice);
    const catalogPrice = catalogPriceRaw || unitPrice;
    if (!unitPrice && qty > 0) {
      unitPrice = round2(exclFromAmounts / qty);
    }
    // Add/Edit mode: keep editor-facing values positive for easier user input.
    if (editableMode) {
      unitPrice = absNum(unitPrice);
    }

    const lineOwnKey = getTxnOwnKey(txn);
    const taxTxn = taxByParent.get(id) || (lineOwnKey ? taxByChildKey.get(lineOwnKey) : undefined);
    const vatPct = editableMode
      ? toNum(
          txn.VATValue ??
            txn.vatValue ??
            taxTxn?.VATValue ??
            taxTxn?.vatValue
        )
      : toNum(
          taxTxn?.VATValue ??
            taxTxn?.vatValue ??
            txn.VATValue ??
            txn.vatValue
        );
    const vatLabel = String(taxTxn?.VAT ?? txn?.VAT ?? "").trim();
    const currency = getDisplayCurrency(invoiceCurrency, txn);

    const exclFromUnit = calcExcl(qty, unitPrice);
    let exclVATRaw = editableMode
      ? exclFromUnit
      : exclFromAmounts !== 0
        ? exclFromAmounts
        : exclFromUnit;
    if (!editableMode && unitPrice !== 0 && exclVATRaw !== 0) {
      unitPrice = round2(Math.abs(unitPrice) * Math.sign(exclVATRaw));
    }
    if (!editableMode && unitPrice !== 0 && exclVATRaw !== 0) {
      const unitSign = Math.sign(unitPrice);
      const exclSign = Math.sign(exclVATRaw);
      if (unitSign !== 0 && exclSign !== 0 && unitSign !== exclSign) {
        exclVATRaw = exclFromUnit;
      }
    }

    const taxShare = asArray<any>(taxTxn?.SHARES?.SHARE)[0] || {};
    const taxAmountRaw = getRevertedDisplayAmount(taxTxn || {}, taxShare, invoiceMeta);
    let taxAmount = taxAmountRaw;
    if (!editableMode && exclVATRaw !== 0 && taxAmount !== 0) {
      const exclSign = Math.sign(exclVATRaw);
      const taxSign = Math.sign(taxAmount);
      if (exclSign !== 0 && taxSign !== 0 && exclSign !== taxSign) {
        taxAmount = round2(Math.abs(taxAmount) * exclSign);
      }
    }
    const inclVATRaw = editableMode
      ? calcIncl(exclVATRaw, vatPct)
      : taxAmount !== 0
        ? round2(exclVATRaw + taxAmount)
        : calcIncl(exclVATRaw, vatPct);
    // Add/Edit mode => positive editable values.
    // Read mode     => keep response/reverted sign.
    const exclVAT = editableMode ? absNum(exclVATRaw) : exclVATRaw;
    const inclVAT = editableMode ? absNum(inclVATRaw) : inclVATRaw;
    const discountPct =
      catalogPrice > 0
        ? round2(((catalogPrice - unitPrice) / catalogPrice) * 100)
        : 0;

    const shareMode = detectShareModeFromTxnShares(shares);
    const rowShares: ControllingShareRow[] = shares.length
      ? shares.map((share: any, index: number) => {
          const percentage = normalizeSharePercentage(share?.percentage);
          const units = round2(toNum(share?.shareUnits ?? share?.units));
          const total = round2(Math.abs(toNum(share?.total)));
          let shareValue = percentage;
          if (shareMode === "value") shareValue = total;
          if (shareMode === "unitsShare" || shareMode === "quantity") shareValue = units;
          return {
            id: String(share?.id || `share-${index + 1}`),
            share: shareValue,
          };
        })
      : [
          { id: "share-1", share: getDefaultSingleShareValue(shareMode, qty, exclVAT) },
        ];

    rows.push({
      id,
      parentId,
      depth,
      childrenIds: childIds,
      isExpanded: true,
      rowType: childIds.length > 0 ? "product" : "simple",
      sortOrder: toNum(txn.orderIndex),
      data: {
        name:
          txn.itemDescription ||
          firstShare?.GLItemName ||
          txn.itemName ||
          txn.transactionsName ||
          "",
        productName: txn.itemName || txn.itemDescription || "",
        itemId: txn.item || "",
        unitPrice,
        catalogPrice: editableMode ? absNum(catalogPrice) : catalogPrice,
        discountPct,
        qty,
        vatPct,
        vatLabel,
        exclVAT,
        inclVAT,
        currency,
        description: txn.description || "",
        itemRefId: txn.itemRefId || "",
        accountId: txn.account || "",
        accountName: txn.accountName || "",
        transactionOwnerId: txn.userId || "",
        transactionOwnerName: txn.userName || "",
        transactionDate:
          txn.transactionDate ||
          invoiceMeta?.dateTransaction ||
          invoiceMeta?.taxationDate ||
          invoiceMeta?.issuedDate ||
          "",
        lineKey: lineOwnKey || id,
        parentLineKey: String(txn?.parentKey ?? "").trim(),
        shareMode,
        shares: rowShares,
      },
    });

    childIds.forEach((cid) => {
      const child = byId.get(cid);
      if (child) walk(child, id, depth + 1);
    });
  };

  roots.forEach((r) => walk(r, null, 0));
  return rows;
};

const applyRowsToInvoiceTransactions = (invoice: any, rows: TreeGridRow[]) => {
  const list: Txn[] = Array.isArray(invoice?.TRANSACTION) ? [...invoice.TRANSACTION] : [];

  const byId = new Map<string, Txn>();
  list.forEach((t) => byId.set(String(t.id), t));

  const stableKeyByRowId = new Map<string, string>();
  rows.forEach((row) => {
    const rowId = String(row.id);
    const existingTxn = byId.get(rowId);
    const stableKey = String(
      row.data?.lineKey ??
      existingTxn?.childKey ??
      existingTxn?.parentKey ??
      rowId
    ).trim();
    stableKeyByRowId.set(rowId, stableKey || rowId);
  });

  rows.forEach((row) => {
    const rowId = String(row.id);
    let txn = byId.get(rowId);

    if (!txn) {
      txn = {
        id: rowId,
        VATTitle: "N",
        orderIndex: String(toNum(row.sortOrder ?? 9999)),
        itemQuantity: "1",
        otherUnitPrice: "0",
        otherAmount: "0",
        otherCurrency: invoice?.currency || "",
        bookkeepingCurrency: invoice?.currency || "",
        account: invoice?.accountBase || "",
        accountName: invoice?.accountBaseName || "",
      };
      list.push(txn);
      byId.set(rowId, txn);
    }

    const parentValue = row.parentId ? String(row.parentId) : "";
    const numericParentId = /^\d+$/.test(parentValue) ? parentValue : undefined;
    const stableChildKey = stableKeyByRowId.get(rowId) || rowId;
    const stableParentKey = parentValue ? stableKeyByRowId.get(parentValue) || parentValue : undefined;
    const shouldPersistParentKey = !!parentValue && !numericParentId;

    txn.id = rowId;
    txn.parent = parentValue || undefined;
    txn.parentId = numericParentId;
    txn.childKey = stableChildKey;
    if (shouldPersistParentKey) {
      txn.parentKey = stableParentKey;
    } else {
      delete txn.parentKey;
    }
    txn.orderIndex = String(toNum(row.sortOrder ?? 0));
    txn.itemName = row.data?.productName || row.data.name;
    txn.itemDescription = row.data.name;
    // Product is optional: if row has no selected product, keep transaction unbound.
    txn.item = row.data?.itemId ? String(row.data.itemId) : "";
    txn.itemQuantity = String(toNum(row.data.qty));
    txn.account = row.data?.accountId ? String(row.data.accountId) : txn.account;
    txn.accountName = row.data?.accountName ?? txn.accountName;
    txn.otherUnitPrice = String(toNum(row.data.unitPrice));
    txn.catalogPrice = String(toNum(row.data.catalogPrice ?? row.data.unitPrice));
    txn.bookkeepingUnitPrice = String(toNum(row.data.unitPrice));
    txn.otherCurrency = row.data?.currency || invoice?.currency || "";
    txn.bookkeepingCurrency = row.data?.currency || invoice?.currency || "";
    txn.VAT = String(row.data?.vatLabel ?? txn.VAT ?? "");
    txn.VATValue = getVatPctFromRowData(row.data);
    txn.vatValue = getVatPctFromRowData(row.data);

    const excl = calcExcl(toNum(row.data.qty), toNum(row.data.unitPrice));
    txn.otherAmount = String(excl);
    txn.description = row.data.description ?? txn.description;
    txn.itemRefId = row.data.itemRefId ?? txn.itemRefId;
    txn.userId = row.data?.transactionOwnerId ?? txn.userId;
    txn.userName = row.data?.transactionOwnerName ?? txn.userName;
    txn.transactionDate = row.data?.transactionDate ?? txn.transactionDate;

    const shareMode = (row.data?.shareMode as TransactionModalDraft["shareMode"]) || "unitsShare";
    const rowShares = Array.isArray(row.data?.shares) ? row.data.shares : [];
    if (rowShares.length > 0) {
      const qty = toNum(row.data?.qty);
      const unitPrice = toNum(row.data?.unitPrice);
      const excl = calcExcl(qty, unitPrice);
      const txnOtherAmount = toNum(txn.otherAmount);
      const shareSign = txnOtherAmount === 0 ? -1 : -Math.sign(txnOtherAmount);

      txn.SHARES = {
        SHARE: rowShares.map((share: any, index: number) => {
          const totalAbs = shareTotalByMode(share, shareMode, qty, unitPrice, excl);
          const percentage = sharePercentByMode(share, shareMode, qty, excl);
          const units = shareUnitsByMode(share, shareMode, qty, unitPrice);
          const signedTotal = round2(Math.abs(totalAbs) * shareSign);
          return {
            GLItem: /^\d+$/.test(String(txn?.id ?? "")) ? String(txn.id) : undefined,
            GLItemName: row.data?.name || txn?.itemName || txn?.itemDescription || "",
            id: share?.id && /^\d+$/.test(String(share.id)) ? String(share.id) : undefined,
            userId: txn?.userId || invoice?.userId,
            userName: txn?.userName || invoice?.userName,
            folder: txn?.folder || invoice?.folder,
            project: txn?.project || invoice?.project,
            activity: txn?.activity || invoice?.activity,
            remove: "0",
            isRemaining: index === rowShares.length - 1 ? "1" : "0",
            units: String(units),
            shareUnits: String(units),
            percentage: String(round2(percentage)),
            total: String(signedTotal),
            bookKeepingAmount: String(signedTotal),
          };
        }),
      };
    }
  });

  return { ...invoice, TRANSACTION: list };
};

const withCalculatedAmounts = (row: TreeGridRow): TreeGridRow => {
  const qty = toNum(row.data?.qty);
  const unitPrice = toNum(row.data?.unitPrice);
  const vatPct = getVatPctFromRowData(row.data);
  const exclVAT = calcExcl(qty, unitPrice);
  const inclVAT = calcIncl(exclVAT, vatPct);

  return {
    ...row,
    data: {
      ...row.data,
      catalogPrice:
        toNum(row.data?.catalogPrice) || toNum(row.data?.unitPrice),
      discountPct: toNum(row.data?.discountPct),
      qty,
      unitPrice,
      vatPct,
      exclVAT,
      inclVAT,
    },
  };
};

const getDefaultContextFromInvoice = (invoice: any): string => {
  return String(
    invoice?.activityName ||
      invoice?.projectName ||
      invoice?.folderName ||
      invoice?.activity ||
      invoice?.project ||
      invoice?.folder ||
      ""
  );
};

const getDefaultModalDraft = (row: TreeGridRow, invoice?: any): TransactionModalDraft => {
  const qty = toNum(row.data?.qty);
  const unitPrice = toNum(row.data?.unitPrice);
  const vatPct = getVatPctFromRowData(row.data);
  const contextFromHeader = getDefaultContextFromInvoice(invoice);
  const shareMode = (row.data?.shareMode as TransactionModalDraft["shareMode"]) || "unitsShare";
  const defaultExcl = calcExcl(qty, unitPrice);
  return {
    itemId: String(row.data?.itemId || ""),
    productName: String(row.data?.productName || row.data?.name || ""),
    itemRefId: String(row.data?.itemRefId || ""),
    name: String(row.data?.name || ""),
    context: String(row.data?.context || contextFromHeader || ""),
    transactionDate: String(
      row.data?.transactionDate ||
        invoice?.dateTransaction ||
        invoice?.taxationDate ||
        invoice?.issuedDate ||
        ""
    ),
    vatType: String(row.data?.vatId || ""),
    vatLabel: String(row.data?.vatLabel || ""),
    catalogPrice: toNum(row.data?.catalogPrice ?? unitPrice),
    discountPct: toNum(row.data?.discountPct ?? 0),
    unitPrice,
    qty,
    vatPct,
    exclVAT: calcExcl(qty, unitPrice),
    description: String(row.data?.description || ""),
    transactionAnalysis: String(row.data?.accountId || ""),
    transactionOwnerId: String(row.data?.transactionOwnerId || row.data?.userId || ""),
    transactionOwnerName: String(
      row.data?.transactionOwnerName || row.data?.transactionOwner || row.data?.userName || ""
    ),
    shareMode,
    shares: Array.isArray(row.data?.shares) && row.data.shares.length
      ? row.data.shares.map((share: any, index: number) => ({
          id: String(share?.id || `share-${index + 1}`),
          share: toNum(share?.share),
        }))
      : [
          { id: "share-1", share: getDefaultSingleShareValue(shareMode, qty, defaultExcl) },
        ],
  };
};

const cloneRow = (row: TreeGridRow): TreeGridRow =>
  JSON.parse(JSON.stringify(row));

const toInputString = (value: number | undefined) =>
  typeof value === "number" && Number.isFinite(value) ? String(value) : "";

const parseInputNumber = (value: string): number | undefined => {
  const raw = String(value ?? "").trim();
  if (!raw) return undefined;
  const parsed = parseFloat(raw.replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : undefined;
};

export default function InvoiceTransactionComponent({
  invoice,
  isEdit,
  isLocked,
  onInvoiceChange,
}: InvoiceTransactionComponentProps) {
  const { t } = useTranslation("invoice");
  const _t = (key: string, defaultValue?: string) =>
    t([`invoice_transaction.${key}`, `add_update_invoice.${key}`], {
      ns: "invoice",
      defaultValue,
    });
  const _tt = (key: string, defaultValue?: string) =>
    t(`invoice_common.${key}`, { ns: "invoice", defaultValue });

  const { itemService, globalService, store } = useDependency();
  const updateInvoice = useCallback(
    (updater: (prev: any) => any) => {
      onInvoiceChange((prev: any) => updater(prev));
    },
    [onInvoiceChange]
  );
  const { currencies } = usePreloadAssets();
  const [rows, setRows] = useState<TreeGridRow[]>([]);
  const [editingNewRowId, setEditingNewRowId] = useState<string | null>(null);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [modalActiveTab, setModalActiveTab] = useState<TransactionModalTab>("basic");
  const [activeModalRowId, setActiveModalRowId] = useState<string | null>(null);
  const [modalDraft, setModalDraft] = useState<TransactionModalDraft | null>(null);
  const modalDraftRef = useRef<TransactionModalDraft | null>(null);
  const [modalOriginalRow, setModalOriginalRow] = useState<TreeGridRow | null>(null);
  const [modalNumericInputs, setModalNumericInputs] = useState<ModalNumericInputs>({
    catalogPrice: "",
    discountPct: "",
    unitPrice: "",
    qty: "",
    vatPct: "",
    exclVAT: "",
  });
  const [selectedShareId, setSelectedShareId] = useState<string | null>(null);
  const [shareEditor, setShareEditor] = useState<ShareEditorState | null>(null);
  const isCommittingModalRef = useRef(false);
  const [gridVersion, setGridVersion] = useState(0);
  const [isMultiAddOpen, setIsMultiAddOpen] = useState(false);
  const [isCompactGrid, setIsCompactGrid] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 1700;
  });

  // Mode switch:
  // - Read mode (detail view): isEdit=false and invoice has id.
  // - Add mode:               invoice has no id.
  // - Edit mode:              isEdit=true for existing invoice.
  // Add/Edit are editable, Detail is read-only.
  const canEdit = isEdit || !invoice?.id;

  const accountOptions = useMemo<AccountOption[]>(() => {
    const pushOption = (
      list: AccountOption[],
      seen: Set<string>,
      idRaw: any,
      nameRaw: any,
      codeRaw?: any
    ) => {
      const id = String(idRaw ?? "").trim();
      const name = String(nameRaw ?? "").trim();
      if (!id || !name || seen.has(id)) return;
      seen.add(id);
      list.push({ id, name, label: name });
    };

    const options: AccountOption[] = [];
    const seen = new Set<string>();

    // Invoice header defaults (N/R/S/T)
    pushOption(options, seen, invoice?.accountBase, invoice?.accountBaseName, invoice?.accountBaseCode || invoice?.accountBaseRefId);
    pushOption(options, seen, invoice?.accountRound, invoice?.accountRoundName, invoice?.accountRoundCode || invoice?.accountRoundRefId);
    pushOption(options, seen, invoice?.accountTotal, invoice?.accountTotalName, invoice?.accountTotalCode || invoice?.accountTotalRefId);
    pushOption(options, seen, invoice?.accountVAT, invoice?.accountVATName, invoice?.accountVATCode || invoice?.accountVATRefId);

    // Existing invoice transaction accounts
    const txns = Array.isArray(invoice?.TRANSACTION) ? invoice.TRANSACTION : [];
    txns.forEach((txn: any) => {
      pushOption(
        options,
        seen,
        txn?.account,
        txn?.accountName,
        txn?.accountCode || txn?.accountRefId || txn?.accountNumber || txn?.refId
      );
    });

    // All journal defaults from organization settings (broader list than current journal only)
    const journalsRaw = store?.session?.organization?.OBJECT_SETTINGS?.FINANCE?.JOURNALS?.JOURNAL;
    const journals = Array.isArray(journalsRaw) ? journalsRaw : journalsRaw ? [journalsRaw] : [];
    journals.forEach((journal: any) => {
      pushOption(
        options,
        seen,
        journal?.accountBase,
        journal?.accountNameBase,
        journal?.accountBaseCode || journal?.accountBaseRefId
      );
      pushOption(
        options,
        seen,
        journal?.accountRound,
        journal?.accountNameRound,
        journal?.accountRoundCode || journal?.accountRoundRefId
      );
      pushOption(
        options,
        seen,
        journal?.accountTotal,
        journal?.accountNameTotal,
        journal?.accountTotalCode || journal?.accountTotalRefId
      );
      pushOption(
        options,
        seen,
        journal?.accountVat,
        journal?.accountNameVat,
        journal?.accountVatCode || journal?.accountVatRefId
      );
    });

    return options;
  }, [invoice, store?.session?.organization?.OBJECT_SETTINGS?.FINANCE?.JOURNALS?.JOURNAL]);

  useEffect(() => {
    const txns = Array.isArray(invoice?.TRANSACTION) ? invoice.TRANSACTION : [];
    setRows(buildTreeRows(txns, invoice?.currency, invoice, canEdit));
  }, [invoice, canEdit]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsCompactGrid(window.innerWidth < 1700);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const activeModalRow = useMemo(
    () => rows.find((row) => String(row.id) === String(activeModalRowId)) || null,
    [rows, activeModalRowId]
  );

  const applyModalDraftToRow = useCallback((row: TreeGridRow, draft: TransactionModalDraft): TreeGridRow => {
    const selectedAccount = accountOptions.find(
      (option) => String(option.id) === String(draft.transactionAnalysis)
    );
    const nextRow = {
      ...row,
      data: {
        ...row.data,
        itemId: draft.itemId,
        productName: draft.productName || row.data?.productName || draft.name,
        itemRefId: draft.itemRefId || "",
        name: draft.name,
        context: draft.context,
        catalogPrice: draft.catalogPrice,
        discountPct: draft.discountPct,
        unitPrice: toNum(draft.unitPrice),
        qty: toNum(draft.qty),
        vatPct: toNum(draft.vatPct),
        vatValue: toNum(draft.vatPct),
        VATValue: toNum(draft.vatPct),
        vatId: draft.vatType,
        vatLabel: String(draft.vatLabel || ""),
        description: draft.description,
        transactionAnalysis: draft.transactionAnalysis,
        accountId: selectedAccount?.id || row.data?.accountId || "",
        accountName: selectedAccount?.name || row.data?.accountName || "",
        transactionOwnerId: draft.transactionOwnerId,
        transactionOwnerName: draft.transactionOwnerName,
        transactionOwner: draft.transactionOwnerName,
        transactionDate: draft.transactionDate,
        shareMode: draft.shareMode,
        shares: draft.shares,
      },
    };
    return withCalculatedAmounts(nextRow);
  }, [accountOptions]);

  const searchItems = useCallback(
    async (filterText: string): Promise<ComboBoxItem[]> => {
      const q = filterText.trim();

      try {
        const byNamePromise = itemService.listItem(
          new ListItemRequest(store.Server, store.SessionId, 15, 0, {
            searchName: q,
            statusShow: "1",
          })
        );
        const byRefPromise =
          q.length > 0
            ? itemService.listItem(
                new ListItemRequest(store.Server, store.SessionId, 15, 0, {
                  refId: q,
                  statusShow: "1",
                })
              )
            : Promise.resolve({ ITEM: [] });

        const [byName, byRef]: any = await Promise.all([byNamePromise, byRefPromise]);

        const results: ComboBoxItem[] = [];

        if (byRef?.ITEM?.length) {
          byRef.ITEM.forEach((item: any) => {
            results.push({
              ...item,
              id: String(item.id),
              productId: item.id,
              label: `${item.refId} (${item.name})`,
              productName: item.name,
              sku: item.refId || "",
              unitPrice: getUnitPriceFromItem(item),
              vatValue: getVatPctFromItem(item),
              vatLabel: getVatLabelFromItem(item),
              currency: getCurrencyFromItem(item, invoice?.currency),
              category: _t("ref_id_match", "Ref ID Match"),
            });
          });
        }

        if (byName?.ITEM?.length) {
          byName.ITEM.forEach((item: any) => {
            if (!results.some((r: any) => r.productId === item.id)) {
              results.push({
                ...item,
                id: String(item.id),
                productId: item.id,
                label: item.name + (item.refId ? ` (${item.refId})` : ""),
                productName: item.name,
                sku: item.refId || "",
                unitPrice: getUnitPriceFromItem(item),
                vatValue: getVatPctFromItem(item),
                vatLabel: getVatLabelFromItem(item),
                currency: getCurrencyFromItem(item, invoice?.currency),
                category: _t("name_match", "Name Match"),
              });
            }
          });
        }

        return results;
      } catch (error) {
        console.error("Item search failed:", error);
        return [];
      }
    },
    [itemService, store.Server, store.SessionId, invoice?.currency, _t]
  );

  const resolveCurrencyLabel = useCallback(
    (currencyValue: any) => {
      const raw = String(currencyValue ?? "").trim();
      if (!raw) return "";
      if (!/^\d+$/.test(raw)) return raw;

      const found = (currencies || []).find((c: any) => String(c?.id) === raw);
      return found?.code || found?.name || raw;
    },
    [currencies]
  );

  const searchVatOptions = useCallback(
    async (filterText: string): Promise<ComboBoxItem[]> => {
      try {
        const response: any = await globalService.listVAT(
          new ListVATRequest(store.Server, store.SessionId, 200, 0)
        );
        const rows = Array.isArray(response?.ROWS) ? response.ROWS : [];
        const q = String(filterText ?? "").trim().toLowerCase();

        return rows
          .map((r: any) => r?.ITEM)
          .filter(Boolean)
          .filter((item: any) => {
            if (!q) return true;
            const name = String(item?.name ?? "").toLowerCase();
            const value = String(item?.value ?? "").toLowerCase();
            return name.includes(q) || value.includes(q);
          })
          .map((item: any) => ({
            id: String(item.id),
            label: String(item.name || ""),
            name: String(item.name || ""),
            value: Number(String(item.value ?? "0").trim()),
          }));
      } catch (error) {
        console.error("VAT search failed:", error);
        return [];
      }
    },
    [globalService, store.Server, store.SessionId]
  );

  const resolveProductDefaultAccount = useCallback(
    (item: any): { id?: string; name?: string } => {
      const docClass = getInvoiceDocumentClass(invoice || {});

      const pickAccount = (idKeys: string[], nameKeys: string[]) => {
        const id = idKeys
          .map((k) => String(item?.[k] ?? "").trim())
          .find((v) => !!v);
        if (!id) return undefined;
        const name = nameKeys
          .map((k) => String(item?.[k] ?? "").trim())
          .find((v) => !!v);
        return { id, name: name || undefined };
      };

      // Product settings according to docs:
      // - Purchase docs: use purchase account on N row
      // - Sales docs: use sales account on N row
      const purchaseAccount = pickAccount(
        [
          "purchaseAccount",
          "purchase_account",
          "purchaseGLAccount",
          "purchaseAccountId",
          "accountPurchase",
          "debitAccount",
        ],
        [
          "purchaseAccountName",
          "purchase_account_name",
          "purchaseGLAccountName",
          "accountPurchaseName",
          "debitAccountName",
        ]
      );
      const salesAccount = pickAccount(
        [
          "salesAccount",
          "sales_account",
          "salesGLAccount",
          "salesAccountId",
          "accountSales",
          "creditAccount",
        ],
        [
          "salesAccountName",
          "sales_account_name",
          "salesGLAccountName",
          "accountSalesName",
          "creditAccountName",
        ]
      );

      if (docClass === "purchase" && purchaseAccount?.id) {
        return purchaseAccount;
      }
      if (docClass === "sales" && salesAccount?.id) {
        return salesAccount;
      }

      // Fallback order if type is unclear
      if (purchaseAccount?.id) return purchaseAccount;
      if (salesAccount?.id) return salesAccount;
      return {};
    },
    [invoice]
  );

  const columns: TreeGridColumn[] = useMemo(
    () => [
      {
        key: "name",
        header: _t("name", "Name"),
        width: isCompactGrid ? "68%" : "60%",
        editable: canEdit,
        editorType: "combobox",
        isTreeColumn: true,
        showFolderIcon: true,
        comboBoxConfig: {
          placeholder: _t("search_item_name_ref", "Search item by name or ref..."),
          menuWidth: 420,
          debounceMs: 250,
          minSearchLength: 2,
          syncCustomInputValue: true,
          loadItems: searchItems,
          groupBy: (item) => (item as any).category || _t("other", "Other"),
          getDisplayValue: (item) => (item as any).productName || item.label,
          getValue: (item) => (item as any).productName || item.label,
          onSelectionChange: (item, row) => {
            if (!item) return {};
            const qty = toNum(row.data?.qty || 1);
            const unitPrice = getUnitPriceFromItem(item as any);
            const vatPct = getVatPctFromItem(item as any);
            const vatLabel = getVatLabelFromItem(item as any);
            const currency = getCurrencyFromItem(item as any, invoice?.currency);
            const productAccount = resolveProductDefaultAccount(item as any);
            const excl = calcExcl(qty, unitPrice);
            const incl = calcIncl(excl, vatPct);
            return {
              name: (item as any).productName || item.label,
              productName: (item as any).productName || item.label,
              itemId: (item as any).productId || (item as any).id || "",
              itemRefId: (item as any).sku || "",
              description: (item as any).description || row.data?.description || "",
              unitPrice,
              qty,
              vatPct,
              vatLabel: vatLabel || row.data?.vatLabel || "",
              vatValue: vatPct,
              VATValue: vatPct,
              exclVAT: excl,
              inclVAT: incl,
              currency,
              accountId: productAccount?.id || row.data?.accountId || "",
              accountName: productAccount?.name || row.data?.accountName || "",
            };
          },
        },
        render: (value: any, row: TreeGridRow) => {
          const name = String(value || "-");
          return (
            <div className={styles.nameCell}>
              <div className={styles.nameTopRow}>
                <span className={styles.nameText} title={name}>
                  {name}
                </span>
              </div>
              {row.data?.accountName ? (
                <div className={styles.nameSubText} title={row.data.accountName}>
                  {row.data.accountName}
                </div>
              ) : (
                <div className={styles.nameSubText} />
              )}
            </div>
          );
        },
      },
      {
        key: "unitPrice",
        header: _t("unit_price", "Unit Price"),
        width: isCompactGrid ? 126 : 140,
        editable: canEdit,
        editorType: "number",
        align: "right",
        numberFormat: { decimals: 2, thousandsSeparator: true },
        format: (value: any, row: TreeGridRow) =>
          formatMoney(value, resolveCurrencyLabel(row.data?.currency)),
        onCommit: (value: any, row: TreeGridRow) => {
          row.data.unitPrice = toNum(value);
          row.data.exclVAT = calcExcl(toNum(row.data.qty), toNum(row.data.unitPrice));
          row.data.inclVAT = calcIncl(toNum(row.data.exclVAT), toNum(row.data.vatPct));
        },
      },
      {
        key: "qty",
        header: _t("qty_short", "Qty."),
        width: isCompactGrid ? 72 : 80,
        editable: canEdit,
        editorType: "number",
        align: "right",
        numberFormat: { decimals: 2, thousandsSeparator: false },
        validate: (value: any) => {
          const n = toNum(value);
          if (!Number.isFinite(n) || n <= 0) return _t("qty_gt_zero", "Qty must be > 0");
          return null;
        },
        onCommit: (value: any, row: TreeGridRow) => {
          row.data.qty = toNum(value);
          row.data.exclVAT = calcExcl(toNum(row.data.qty), toNum(row.data.unitPrice));
          row.data.inclVAT = calcIncl(toNum(row.data.exclVAT), toNum(row.data.vatPct));
        },
      },
      {
        key: "vatPct",
        header: _t("vat", "VAT"),
        width: isCompactGrid ? 96 : 110,
        editable: canEdit,
        editorType: "combobox",
        align: "right",
        comboBoxConfig: {
          placeholder: _t("vat_type", "VAT type"),
          menuWidth: 260,
          debounceMs: 200,
          minSearchLength: 0,
          commitOnSelection: true,
          loadItems: searchVatOptions,
          getDisplayValue: (item) => String((item as any).name || item.label || ""),
          getValue: (item) => Number((item as any).value ?? 0),
          onSelectionChange: (item, row) => {
            const vatPct = Number((item as any).value ?? 0);
            const qty = toNum(row.data?.qty);
            const unitPrice = toNum(row.data?.unitPrice);
            const excl = calcExcl(qty, unitPrice);
            const incl = calcIncl(excl, vatPct);
            return {
              vatId: String((item as any).id || ""),
              vatLabel: String((item as any).name || item.label || ""),
              vatPct,
              vatValue: vatPct,
              VATValue: vatPct,
              exclVAT: excl,
              inclVAT: incl,
            };
          },
        },
        format: (_value: any, row: TreeGridRow) => {
          const label = String(row.data?.vatLabel || "").trim();
          if (label) return label;
          return `${getVatPctFromRowData(row.data).toFixed(0)}%`;
        },
        onCommit: (value: any, row: TreeGridRow) => {
          const vatPct = toNum(value);
          row.data.vatPct = vatPct;
          row.data.vatValue = vatPct;
          row.data.VATValue = vatPct;
          row.data.exclVAT = calcExcl(toNum(row.data.qty), toNum(row.data.unitPrice));
          row.data.inclVAT = calcIncl(toNum(row.data.exclVAT), vatPct);
        },
      },
      {
        key: "exclVAT",
        header: _t("excl_vat", "excl. VAT"),
        width: isCompactGrid ? 136 : 150,
        editable: false,
        align: "right",
        showTotal: true,
        totalLabel: _t("total", "Total:"),
        totalFormat: (total: number) => {
          return `${total.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        },
        format: (value: any, row: TreeGridRow) =>
          formatMoney(value, resolveCurrencyLabel(row.data?.currency || invoice?.currency)),
      },
      {
        key: "inclVAT",
        header: _t("incl_vat", "incl. VAT"),
        width: 150,
        hidden: isCompactGrid,
        editable: false,
        align: "right",
        showTotal: true,
        totalLabel: "",
        totalFormat: (total: number) => {
          return `${total.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        },
        format: (value: any, row: TreeGridRow) =>
          formatMoney(value, resolveCurrencyLabel(row.data?.currency || invoice?.currency)),
      },
    ],
    [canEdit, isCompactGrid, searchItems, searchVatOptions, resolveCurrencyLabel, resolveProductDefaultAccount, invoice?.currency, _t]
  );

  const handleRowUpdate = useCallback(
    async (row: TreeGridRow) => {
      setEditingNewRowId(null);
      const calculatedRow = withCalculatedAmounts(row);
      setRows((prev) => {
        const exists = prev.some((r) => r.id === calculatedRow.id);
        const nextRows = exists
          ? prev.map((r) => (r.id === calculatedRow.id ? calculatedRow : r))
          : [...prev, calculatedRow];
        updateInvoice((prevInvoice) =>
          applyRowsToInvoiceTransactions(prevInvoice, nextRows)
        );
        return nextRows;
      });

      ToastQueue.positive(_t("transaction_updated", "Transaction updated"), { timeout: 1500 });
    },
    [updateInvoice, _t]
  );

  const handleCellValueChange = useCallback(
    (rowId: string, columnKey: string, value: any) => {
      if (!(columnKey === "name" || columnKey === "qty" || columnKey === "unitPrice" || columnKey === "vatPct")) {
        return;
      }

      setRows((prev) => {
        const nextRows = prev.map((r) => {
          if (r.id !== rowId) return r;
          const nextRow = {
            ...r,
            data: {
              ...r.data,
              [columnKey]: value,
              // If user types custom name (no picked product), clear product binding.
            },
          };
          return withCalculatedAmounts(nextRow);
        });

        updateInvoice((prevInvoice) =>
          applyRowsToInvoiceTransactions(prevInvoice, nextRows)
        );
        return nextRows;
      });
    },
    [updateInvoice]
  );

  const handleMultiFieldUpdate = useCallback(
    (rowId: string, changes: Partial<any>) => {
      setRows((prev) => {
        const nextRows = prev.map((r) => {
          if (r.id !== rowId) return r;
          const nextRow = {
            ...r,
            data: {
              ...r.data,
              ...changes,
            },
          };
          return withCalculatedAmounts(nextRow);
        });

        updateInvoice((prevInvoice) =>
          applyRowsToInvoiceTransactions(prevInvoice, nextRows)
        );
        return nextRows;
      });
    },
    [updateInvoice]
  );

  const handleRowCreate = useCallback(() => {
    if (!canEdit) return;

    const newId = `new-${Date.now()}`;
    const currency = invoice?.currency || "";

    const newRow: TreeGridRow = {
      id: newId,
      parentId: null,
      depth: 0,
      childrenIds: [],
      isExpanded: false,
      rowType: "simple",
      sortOrder: 9999,
      data: {
        name: _t("new_item", "New item"),
        productName: "",
        unitPrice: 0,
        qty: 1,
        vatPct: 0,
        exclVAT: 0,
        inclVAT: 0,
        currency,
        description: "",
        itemRefId: "",
        accountId: invoice?.accountBase || "",
        accountName: invoice?.accountBaseName || "",
        itemId: "",
      },
    };

    setRows((prev) => {
      const nextRows = [...prev, newRow];
      updateInvoice((prevInvoice) =>
        applyRowsToInvoiceTransactions(prevInvoice, nextRows)
      );
      return nextRows;
    });
    setEditingNewRowId(newId);
  }, [canEdit, invoice, updateInvoice, _t]);

  const handleMultiProductSave = useCallback(
    async (selectedProducts: SelectedProduct[]) => {
      if (!canEdit || !selectedProducts?.length) return;

      const currencyFallback = invoice?.currency || "";
      setRows((prev) => {
        const baseSortOrder =
          prev.reduce((max, row) => Math.max(max, toNum(row.sortOrder)), -1) + 1;
        const batchSeed = Date.now();
        const newRows: TreeGridRow[] = [];

        selectedProducts.forEach((product, index) => {
          const parentId = `new-${batchSeed}-${index}`;
          const productAccount = resolveProductDefaultAccount(product as any);
          const quantity = toNum(product.quantity || 1) || 1;
          const unitPrice = getUnitPriceFromItem(product as any);
          const vatPct = toNum(product.vatValue);
          const vatLabel = String(product.vatName || "").trim();
          const currency = getCurrencyFromItem(product as any, currencyFallback);
          const componentRows: TreeGridRow[] = (product.components || []).map((component, childIndex) => {
            const childId = `${parentId}-child-${childIndex}`;
            const componentQty = toNum(component.countMin || 1) || 1;
            const componentUnitPrice = toNum(component.amount);
            const componentVatPct = toNum(component.vatValue);
            const componentVatLabel = String(component.vatName || "").trim();
            const componentCurrency = getCurrencyFromItem(component as any, currency);

            return withCalculatedAmounts({
              id: childId,
              parentId,
              depth: 1,
              childrenIds: [],
              isExpanded: false,
              rowType: "simple",
              sortOrder: childIndex,
              data: {
                name: String(component.componentName || _t("new_item", "New item")),
                productName: String(component.componentName || ""),
                itemId: String(component.component || ""),
                itemRefId: "",
                unitPrice: componentUnitPrice,
                catalogPrice: componentUnitPrice,
                discountPct: 0,
                qty: componentQty,
                vatPct: componentVatPct,
                vatValue: componentVatPct,
                VATValue: componentVatPct,
                vatLabel: componentVatLabel,
                exclVAT: 0,
                inclVAT: 0,
                currency: componentCurrency,
                description: String(component.componentName || ""),
                accountId: productAccount?.id || invoice?.accountBase || "",
                accountName: productAccount?.name || invoice?.accountBaseName || "",
              },
            });
          });

          newRows.push(
            withCalculatedAmounts({
              id: parentId,
              parentId: null,
              depth: 0,
              childrenIds: componentRows.map((row) => String(row.id)),
              isExpanded: componentRows.length > 0,
              rowType: componentRows.length > 0 ? "product" : "simple",
              sortOrder: baseSortOrder + index,
              data: {
                name: String(product.productName || product.description || _t("new_item", "New item")),
                productName: String(product.productName || ""),
                itemId: String(product.partID || product.id || ""),
                itemRefId: String(product.partNumber || ""),
                unitPrice,
                catalogPrice: unitPrice,
                discountPct: 0,
                qty: quantity,
                vatPct,
                vatValue: vatPct,
                VATValue: vatPct,
                vatLabel,
                exclVAT: 0,
                inclVAT: 0,
                currency,
                description: String(product.description || ""),
                accountId: productAccount?.id || invoice?.accountBase || "",
                accountName: productAccount?.name || invoice?.accountBaseName || "",
              },
            }),
            ...componentRows
          );
        });

        const nextRows = [...prev, ...newRows];
        updateInvoice((prevInvoice) =>
          applyRowsToInvoiceTransactions(prevInvoice, newRows)
        );
        return nextRows;
      });

      ToastQueue.positive(
        _t("products_added", "{{count}} products added", {
          count: selectedProducts.length,
          defaultValue: `${selectedProducts.length} products added`,
        }),
        { timeout: 2000 }
      );
      setIsMultiAddOpen(false);
    },
    [canEdit, invoice, resolveProductDefaultAccount, updateInvoice, _t]
  );

  const handleRowDelete = useCallback(
    (rowId: string) => {
      if (!canEdit) return;

      const rowIdStr = String(rowId);
      const idsToDelete = new Set<string>([rowIdStr]);

      // Include all descendants from current visible tree.
      const queue = [rowIdStr];
      while (queue.length) {
        const currentId = queue.shift()!;
        rows.forEach((r) => {
          if (String(r.parentId ?? "") === currentId && !idsToDelete.has(String(r.id))) {
            const childId = String(r.id);
            idsToDelete.add(childId);
            queue.push(childId);
          }
        });
      }

      setRows((prev) => prev.filter((r) => !idsToDelete.has(String(r.id)) && !idsToDelete.has(String(r.parentId ?? ""))));

      updateInvoice((prevInvoice) => {
        const nextInvoice = { ...prevInvoice };
        if (Array.isArray(nextInvoice?.TRANSACTION)) {
          const deletedParentKeys = new Set<string>();
          nextInvoice.TRANSACTION.forEach((t: any) => {
            if (idsToDelete.has(String(t?.id ?? ""))) {
              const pk = String(t?.parentKey ?? "").trim();
              if (pk) deletedParentKeys.add(pk);
            }
          });

          nextInvoice.TRANSACTION = nextInvoice.TRANSACTION.flatMap((t: any) => {
            const idStr = String(t?.id ?? "");
            const isTargetById = idsToDelete.has(idStr);
            const isTargetByParent = idsToDelete.has(String(t?.parent ?? ""));
            const isTargetByChildKey = deletedParentKeys.has(String(t?.childKey ?? "").trim());
            const shouldDelete = isTargetById || isTargetByParent || isTargetByChildKey;

            if (!shouldDelete) {
              return [t];
            }

            // Local unsaved rows should be removed from client state.
            if (!/^\d+$/.test(idStr)) {
              return [];
            }

            // Persisted rows are sent with remove=1 (legacy-compatible behavior).
            return [{ ...t, remove: "1" }];
          });
        }
        return nextInvoice;
      });

      ToastQueue.info(_t("item_deleted", "Item deleted"), { timeout: 1500 });
    },
    [canEdit, updateInvoice, rows, _t]
  );

  const handleEditCancel = useCallback(
    (rowId: string) => {
      if (!(rowId.startsWith("new-") || rowId.startsWith("row_"))) {
        return;
      }

      setRows((prev) => prev.filter((r) => r.id !== rowId));

      updateInvoice((prevInvoice) => {
        const nextInvoice = { ...prevInvoice };
        if (Array.isArray(nextInvoice?.TRANSACTION)) {
          nextInvoice.TRANSACTION = nextInvoice.TRANSACTION.filter(
            (t: any) => String(t.id) !== String(rowId)
          );
        }
        return nextInvoice;
      });
      setEditingNewRowId(null);
    },
    [updateInvoice]
  );

  const handleRowMove = useCallback(
    (rowId: string, newParentId: string | null, targetIndex: number) => {
      if (!canEdit) return;

      setRows((prevRows) => {
        const rowsCopy = prevRows.map((row) => ({
          ...row,
          parentId: row.parentId,
          childrenIds: [...row.childrenIds],
        }));

        const movedRowId = String(rowId);
        const rowsById = new Map(rowsCopy.map((row) => [String(row.id), row]));
        const movedRow = rowsById.get(movedRowId);
        if (!movedRow) {
          return prevRows;
        }

        if (newParentId && newParentId === movedRowId) {
          return prevRows;
        }

        let currentParentId = newParentId;
        while (currentParentId) {
          if (currentParentId === movedRowId) {
            return prevRows;
          }
          currentParentId = rowsById.get(String(currentParentId))?.parentId ?? null;
        }

        const previousParentId = movedRow.parentId;

        const reorderSiblings = (parentId: string | null, orderedIds?: string[]) => {
          const siblingIds =
            orderedIds ??
            rowsCopy
              .filter((row) => row.parentId === parentId)
              .sort((a, b) => toNum(a.sortOrder) - toNum(b.sortOrder))
              .map((row) => String(row.id));

          siblingIds.forEach((siblingId, index) => {
            const sibling = rowsById.get(siblingId);
            if (sibling) {
              sibling.sortOrder = index;
            }
          });
        };

        if (previousParentId !== null) {
          const previousParent = rowsById.get(String(previousParentId));
          if (previousParent) {
            previousParent.childrenIds = previousParent.childrenIds.filter(
              (childId) => String(childId) !== movedRowId
            );
            reorderSiblings(previousParent.id, previousParent.childrenIds.map(String));
          }
        }

        movedRow.parentId = newParentId;

        if (newParentId !== null) {
          const newParent = rowsById.get(String(newParentId));
          if (!newParent) {
            return prevRows;
          }

          const nextChildren = newParent.childrenIds
            .map(String)
            .filter((childId) => childId !== movedRowId);
          const insertIndex = Math.max(0, Math.min(targetIndex, nextChildren.length));
          nextChildren.splice(insertIndex, 0, movedRowId);
          newParent.childrenIds = nextChildren;
          reorderSiblings(newParent.id, nextChildren);
        } else {
          const rootIds = rowsCopy
            .filter((row) => row.parentId === null && String(row.id) !== movedRowId)
            .sort((a, b) => toNum(a.sortOrder) - toNum(b.sortOrder))
            .map((row) => String(row.id));
          const insertIndex = Math.max(0, Math.min(targetIndex, rootIds.length));
          rootIds.splice(insertIndex, 0, movedRowId);
          reorderSiblings(null, rootIds);
        }

        const rebuiltRows = buildTreeStructure(rowsCopy);
        const rebuiltRowsById = new Map(rebuiltRows.map((row) => [String(row.id), row]));
        const normalizedRows = rebuiltRows.map((row) => ({
          ...row,
          depth: calculateDepth(String(row.id), rebuiltRowsById),
          data: {
            ...row.data,
          },
        }));

        updateInvoice((prevInvoice) =>
          applyRowsToInvoiceTransactions(prevInvoice, normalizedRows)
        );

        return normalizedRows;
      });
    },
    [canEdit, updateInvoice]
  );

  const closeTransactionModal = useCallback(() => {
    setIsTransactionModalOpen(false);
    setModalDraft(null);
    modalDraftRef.current = null;
    setModalNumericInputs({
      catalogPrice: "",
      discountPct: "",
      unitPrice: "",
      qty: "",
      vatPct: "",
      exclVAT: "",
    });
    setActiveModalRowId(null);
    setModalActiveTab("basic");
    setModalOriginalRow(null);
    setSelectedShareId(null);
    setShareEditor(null);
  }, []);

  const handleCancelTransactionModal = useCallback(() => {
    if (isCommittingModalRef.current) {
      isCommittingModalRef.current = false;
      return;
    }
    if (modalOriginalRow && activeModalRowId) {
      setRows((prev) => {
        return prev.map((row) =>
          String(row.id) === String(activeModalRowId) ? cloneRow(modalOriginalRow) : row
        );
      });
    }
    closeTransactionModal();
  }, [activeModalRowId, closeTransactionModal, modalOriginalRow]);

  const openTransactionModal = useCallback(
    (row: TreeGridRow) => {
      isCommittingModalRef.current = false;
      // Exit inline row editor so modal changes become the source of truth.
      setEditingNewRowId(null);
      const draft = getDefaultModalDraft(row, invoice);
      modalDraftRef.current = draft;
      setActiveModalRowId(String(row.id));
      setModalOriginalRow(cloneRow(row));
      setModalDraft(draft);
      setModalNumericInputs({
        catalogPrice: toInputString(draft.catalogPrice),
        discountPct: toInputString(draft.discountPct),
        unitPrice: toInputString(draft.unitPrice),
        qty: toInputString(draft.qty),
        vatPct: toInputString(draft.vatPct),
        exclVAT: toInputString(draft.exclVAT),
      });
      setModalActiveTab("basic");
      setIsTransactionModalOpen(true);
    },
    [invoice]
  );

  const updateModalDraft = useCallback(
    (changes: Partial<TransactionModalDraft>) => {
      const base = modalDraftRef.current;
      if (!base) return;
      const next = { ...base, ...changes };
      const exclVAT = calcExcl(toNum(next.qty), toNum(next.unitPrice));
      const nextWithDerived = { ...next, exclVAT };
      modalDraftRef.current = nextWithDerived;
      setModalDraft(nextWithDerived);
    },
    []
  );

  const addModalShareRow = useCallback(() => {
    const nextId = `share-${Date.now()}`;
    setSelectedShareId(nextId);
    setShareEditor({
      mode: "add",
      rowId: nextId,
      value: "",
    });
  }, []);

  const startEditModalShareRow = useCallback((shareId: string) => {
    setSelectedShareId(shareId);
    setModalDraft((prev) => {
      if (!prev) return prev;
      const share = prev.shares.find((s) => s.id === shareId);
      setShareEditor({
        mode: "edit",
        rowId: shareId,
        value: share ? String(toNum(share.share)) : "",
      });
      return prev;
    });
  }, []);

  const cancelShareEditor = useCallback(() => {
    setShareEditor(null);
  }, []);

  const saveShareEditor = useCallback(() => {
    if (!shareEditor) return;
    const parsed = parseInputNumber(shareEditor.value);
    const value = round2(Math.abs(toNum(parsed ?? 0)));

    setModalDraft((prev) => {
      if (!prev) return prev;
      if (shareEditor.mode === "add") {
        return {
          ...prev,
          shares: [...prev.shares, { id: shareEditor.rowId, share: value }],
        };
      }
      return {
        ...prev,
        shares: prev.shares.map((share) =>
          share.id === shareEditor.rowId ? { ...share, share: value } : share
        ),
      };
    });
    setSelectedShareId(shareEditor.rowId);
    setShareEditor(null);
  }, [shareEditor]);

  const removeSelectedShareRow = useCallback(() => {
    if (!selectedShareId) return;
    setModalDraft((prev) => {
      if (!prev) return prev;
      if (prev.shares.length <= 1) return prev;
      const next = prev.shares.filter((share) => share.id !== selectedShareId);
      return {
        ...prev,
        shares: next.length ? next : prev.shares,
      };
    });
    setSelectedShareId(null);
    setShareEditor(null);
  }, [selectedShareId]);

  const recountModalShares = useCallback(() => {
    setModalDraft((prev) => {
      if (!prev || prev.shares.length === 0) return prev;
      const qty = toNum(parseInputNumber(modalNumericInputs.qty));
      const unitPrice = toNum(parseInputNumber(modalNumericInputs.unitPrice));
      const excl = calcExcl(qty, unitPrice);

      const nextShares = prev.shares.map((share) => ({ ...share }));
      if (nextShares.length === 1) {
        nextShares[0].share = prev.shareMode === "percentShare"
          ? 100
          : prev.shareMode === "value"
            ? Math.abs(excl)
            : qty;
        return { ...prev, shares: nextShares };
      }

      if (prev.shareMode === "percentShare") {
        const sumOthers = nextShares.slice(0, -1).reduce((acc, s) => acc + toNum(s.share), 0);
        nextShares[nextShares.length - 1].share = round2(100 - sumOthers);
      } else if (prev.shareMode === "value") {
        const target = Math.abs(excl);
        const sumOthers = nextShares.slice(0, -1).reduce((acc, s) => acc + toNum(s.share), 0);
        nextShares[nextShares.length - 1].share = round2(target - sumOthers);
      } else {
        const target = qty;
        const sumOthers = nextShares.slice(0, -1).reduce((acc, s) => acc + toNum(s.share), 0);
        nextShares[nextShares.length - 1].share = round2(target - sumOthers);
      }

      return { ...prev, shares: nextShares };
    });
  }, [modalNumericInputs.qty, modalNumericInputs.unitPrice]);

  const getShareHeaderByMode = useCallback((mode: TransactionModalDraft["shareMode"]) => {
    if (mode === "quantity") return _t("quantity", "Quantity");
    if (mode === "unitsShare") return _t("units_share", "Units Share");
    if (mode === "percentShare") return _t("percent_share", "% Share");
    return _t("value", "Value");
  }, [_t]);

  const handleModalVatTypeChange = useCallback((selected: InvoiceVatOption | null) => {
    const vat = selected ? toNum(selected.value) : 0;
    updateModalDraft({
      vatType: selected ? String(selected.id) : "",
      vatLabel: selected?.name || "",
      vatPct: vat,
    });
    setModalNumericInputs((prev) => ({ ...prev, vatPct: String(vat) }));
  }, [updateModalDraft]);

  const saveTransactionModal = useCallback(() => {
    const currentDraft = modalDraftRef.current || modalDraft;
    if (!activeModalRowId || !currentDraft) {
      closeTransactionModal();
      return;
    }

    const qty = parseInputNumber(modalNumericInputs.qty);
    const unitPrice = parseInputNumber(modalNumericInputs.unitPrice);
    const vatPct = parseInputNumber(modalNumericInputs.vatPct);
    const catalogPrice = parseInputNumber(modalNumericInputs.catalogPrice);
    const discountPct = parseInputNumber(modalNumericInputs.discountPct);
    const exclVATInput = parseInputNumber(modalNumericInputs.exclVAT);
    const exclComputed = calcExcl(toNum(qty), toNum(unitPrice));
    const exclVAT = exclVATInput ?? exclComputed;
    const finalUnitPrice =
      unitPrice ??
      (qty && qty !== 0 ? round2(exclVAT / qty) : undefined);

    const finalDraft: TransactionModalDraft = {
      ...currentDraft,
      qty,
      unitPrice: finalUnitPrice,
      vatPct,
      catalogPrice,
      discountPct,
      exclVAT,
    };

    // Ensure row isn't stuck in inline edit mode after modal save.
    setEditingNewRowId(null);
    // Reset TreeGrid internal editor cache (editingValues) so updated row values render.
    setGridVersion((v) => v + 1);
    isCommittingModalRef.current = true;
    setRows((prev) => {
      const nextRows = prev.map((row) =>
        String(row.id) === String(activeModalRowId)
          ? applyModalDraftToRow(row, finalDraft)
          : row
      );
      updateInvoice((prevInvoice) =>
        applyRowsToInvoiceTransactions(prevInvoice, nextRows)
      );
      return nextRows;
    });

    closeTransactionModal();
  }, [activeModalRowId, applyModalDraftToRow, closeTransactionModal, modalDraft, modalNumericInputs, updateInvoice]);

  const modalShareTotal = useMemo(() => {
    if (!modalDraft) return 0;
    const qty = toNum(parseInputNumber(modalNumericInputs.qty));
    const unitPrice = toNum(parseInputNumber(modalNumericInputs.unitPrice));
    const excl = calcExcl(qty, unitPrice);
    const shareSum = modalDraft.shares.reduce(
      (sum, share) => sum + sharePercentByMode(share, modalDraft.shareMode, qty, excl),
      0
    );
    return round2(shareSum);
  }, [modalDraft, modalNumericInputs.qty, modalNumericInputs.unitPrice]);

  const modalExclValue = useMemo(() => {
    const parsedExcl = parseInputNumber(modalNumericInputs.exclVAT);
    if (parsedExcl !== undefined) return parsedExcl;
    return calcExcl(
      toNum(parseInputNumber(modalNumericInputs.qty)),
      toNum(parseInputNumber(modalNumericInputs.unitPrice))
    );
  }, [modalNumericInputs.exclVAT, modalNumericInputs.qty, modalNumericInputs.unitPrice]);

  return (
    <Flex direction="column" gap="size-200" width="100%">
      <View width="100%">
        <TreeGridComponent
          key={`invoice-transactions-${gridVersion}`}
          title={undefined}
          showToolbar={false}
          className={styles.figmaGrid}
          gridId="invoice-transactions"
          columns={columns}
          data={rows}
          defaultEditingRowId={editingNewRowId || undefined}
          showSelection={false}
          showSelectAll={false}
          enableDragDrop={true}
          enableKeyboardNav={true}
          enableInlineEdit={canEdit}
          pageSize={50}
          indentPerLevel={24}
          maxDepth={6}
          showGridLines={false}
          showHover={true}
          showInfoIcon={true}
          showDeleteIcon={canEdit}
          infoIconVariant={canEdit ? "edit" : "info"}
          disableDoubleClickEdit={!canEdit}
          showDeleteConfirmation={false}
          onRowUpdate={handleRowUpdate}
          onRowCreate={() => {}}
          onRowDelete={handleRowDelete}
          onEditCancel={handleEditCancel}
          onCellValueChange={handleCellValueChange}
          onMultiFieldUpdate={handleMultiFieldUpdate}
          onRowMove={handleRowMove}
          onInfoClick={(_rowId, row) => openTransactionModal(row)}
          onRowDoubleClick={!canEdit ? (row) => openTransactionModal(row) : undefined}
          onDeleteClick={(rowId) => handleRowDelete(rowId)}
          emptyMessage={_t("no_items", "No items")}
        />
      </View>
      <View width="100%" UNSAFE_className={styles.footerBar}>
        {canEdit && (
          <Flex gap="size-150" wrap>
            <Button
              variant="cta"
              UNSAFE_className={styles.addBtn}
              onPress={handleRowCreate}
            >
              <span className={styles.addIcon}>+</span>
              {_tt("button_add_item", "Add item")}
            </Button>
            <Button
              variant="cta"
              UNSAFE_className={styles.addBtn}
              onPress={() => setIsMultiAddOpen(true)}
            >
              <span className={styles.addIcon}>+</span>
              {_t("button_multi_add", "Multi add")}
            </Button>
          </Flex>
        )}
      </View>

      <DialogContainer onDismiss={handleCancelTransactionModal}>
        {isTransactionModalOpen && modalDraft && (
          <>
            <Dialog
              size="L"
              UNSAFE_className={styles.transactionModal}
            >
              <Heading>{_t("attribute_information", "Attribute information")}</Heading>
              <Content UNSAFE_className={styles.modalContent}>
                <Tabs
                  selectedKey={modalActiveTab}
                  onSelectionChange={(key) => setModalActiveTab(String(key) as TransactionModalTab)}
                  aria-label={_t("transaction_detail_tabs", "Transaction detail tabs")}
                  isEmphasized
                >
                  <TabList>
                    <Item key="basic">{_tt("tab_basic", "Basic")}</Item>
                    <Item key="advanced">{_tt("tab_advanced", "Advanced")}</Item>
                    <Item key="controlling">{_tt("tab_controlling", "Controlling")}</Item>
                  </TabList>
                  <TabPanels>
                  <Item key="basic">
                    <Flex direction="column" gap="size-200">
                      <Flex direction={{ base: "column", L: "row" }} gap="size-200" width="100%">
                        <View width={{ base: "100%", L: "50%" }}>
                          <ProductDropDown
                            selectedProduct={modalDraft.itemId}
                            productName={modalDraft.productName || modalDraft.name}
                            isDisabled={!canEdit}
                            width="100%"
                            label={_t("product", "Product")}
                            onInputValueChange={() => {
                              // Keep Name field user-driven; combobox typing is only for product search.
                            }}
                            onChange={(selected: any) => {
                              if (!selected) {
                                updateModalDraft({ itemId: "", itemRefId: "", productName: "" });
                                return;
                              }

                              const unitPrice = getUnitPriceFromItem(selected);
                              const vatPct = getVatPctFromItem(selected);
                              const vatLabel = getVatLabelFromItem(selected);
                              const typedQty = parseInputNumber(modalNumericInputs.qty);
                              const draftQty = toNum(modalDraft?.qty ?? 0);
                              const selectedQty = (typedQty ?? draftQty) || 1;
                              const excl = calcExcl(selectedQty, unitPrice);
                              const currentName = String(modalDraft?.name ?? "").trim();
                              const hasBoundProduct = !!String(modalDraft?.itemId ?? "").trim();
                              const originalName = String(modalOriginalRow?.data?.name ?? "").trim();
                              const previousItemId = String(modalDraft?.itemId ?? "").trim();
                              const nextItemId = String(selected.id || "").trim();
                              const isSwitchingProduct =
                                !!previousItemId && !!nextItemId && previousItemId !== nextItemId;
                              const defaultNames = new Set([
                                "new item",
                                String(_t("new_item", "New item")).trim().toLowerCase(),
                              ]);
                              const selectedName = String(
                                selected.name || selected.productName || selected.label || ""
                              ).trim();
                              const shouldOverwriteName =
                                !currentName ||
                                (!hasBoundProduct &&
                                  (defaultNames.has(currentName.toLowerCase()) ||
                                    currentName === originalName)) ||
                                (isSwitchingProduct && currentName === originalName);

                              updateModalDraft({
                                itemId: String(selected.id || ""),
                                productName: selectedName,
                                itemRefId: String(selected.refId || selected.sku || ""),
                                // Preserve manual names, but replace default placeholder names.
                                name: shouldOverwriteName
                                  ? selectedName
                                  : currentName,
                                unitPrice,
                                qty: selectedQty,
                                vatPct,
                                vatLabel: vatLabel || modalDraft?.vatLabel || "",
                                catalogPrice: unitPrice,
                                description: String(
                                  selected.description || modalDraft?.description || ""
                                ),
                              });

                              setModalNumericInputs((prev) => ({
                                ...prev,
                                unitPrice: String(unitPrice),
                                qty: String(selectedQty),
                                vatPct: String(vatPct),
                                catalogPrice: String(unitPrice),
                                discountPct: "0",
                                exclVAT: String(excl),
                              }));
                            }}
                          />
                        </View>
                        <View width={{ base: "100%", L: "50%" }}>
                          <TextField
                            label={_t("context", "Context")}
                            width="100%"
                            value={modalDraft.context}
                            onChange={(value) => updateModalDraft({ context: value })}
                          />
                        </View>
                      </Flex>

                      <TextField
                        label={_t("name", "Name")}
                        width="100%"
                        value={modalDraft.name}
                        onChange={(value) => updateModalDraft({ name: value })}
                      />

                      <Divider size="S" />

                      <Flex direction={{ base: "column", L: "row" }} gap="size-200">
                        <View width={{ base: "100%", L: "50%" }}>
                          <InvoiceVatDropDown
                            label={_t("vat_type", "VAT type")}
                            width={{ base: "100%", L: "100%" }}
                            selectedVat={modalDraft.vatType}
                            selectedVatValue={modalNumericInputs.vatPct}
                            onChange={handleModalVatTypeChange}
                            isDisabled={!canEdit}
                          />
                        </View>
                        <View width={{ base: "100%", L: "50%" }} />
                      </Flex>

                      <Flex direction={{ base: "column", L: "row" }} gap="size-200">
                        <View width={{ base: "100%", L: "50%" }}>
                          <TextField
                            label={_t("catalog_price", "Catalog price")}
                            width="100%"
                            value={modalNumericInputs.catalogPrice}
                            onChange={(value) => {
                              setModalNumericInputs((prev) => {
                                const catalog = parseInputNumber(value);
                                const discount = parseInputNumber(prev.discountPct);
                                const qty = parseInputNumber(prev.qty);

                                let nextUnit = prev.unitPrice;
                                if (
                                  catalog !== undefined &&
                                  discount !== undefined &&
                                  discount >= 0
                                ) {
                                  const unit = round2(catalog * (1 - discount / 100));
                                  nextUnit = String(unit);
                                }

                                const up = parseInputNumber(nextUnit);
                                const excl =
                                  qty !== undefined && up !== undefined
                                    ? calcExcl(qty, up)
                                    : prev.exclVAT;

                                return {
                                  ...prev,
                                  catalogPrice: value,
                                  unitPrice: nextUnit,
                                  exclVAT: String(excl),
                                };
                              });
                            }}
                          />
                        </View>
                        <View width={{ base: "100%", L: "50%" }}>
                          <TextField
                            label={_t("discount_pct", "Discount %")}
                            width="100%"
                            value={modalNumericInputs.discountPct}
                            onChange={(value) => {
                              setModalNumericInputs((prev) => {
                                const discount = parseInputNumber(value);
                                const catalog = parseInputNumber(prev.catalogPrice);
                                const qty = parseInputNumber(prev.qty);

                                let nextUnit = prev.unitPrice;
                                if (
                                  discount !== undefined &&
                                  catalog !== undefined &&
                                  discount >= 0
                                ) {
                                  const unit = round2(catalog * (1 - discount / 100));
                                  nextUnit = String(unit);
                                }

                                const up = parseInputNumber(nextUnit);
                                const excl =
                                  qty !== undefined && up !== undefined
                                    ? calcExcl(qty, up)
                                    : prev.exclVAT;

                                return {
                                  ...prev,
                                  discountPct: value,
                                  unitPrice: nextUnit,
                                  exclVAT: String(excl),
                                };
                              });
                            }}
                          />
                        </View>
                      </Flex>

                      <Flex direction={{ base: "column", L: "row" }} gap="size-200">
                        <View width={{ base: "100%", L: "50%" }}>
                          <TextField
                            label={_t("unit_price", "Unit price")}
                            width="100%"
                            value={modalNumericInputs.unitPrice}
                            onChange={(value) => {
                              setModalNumericInputs((prev) => {
                                const qty = parseInputNumber(prev.qty);
                                const up = parseInputNumber(value);
                                const excl = qty !== undefined && up !== undefined ? calcExcl(qty, up) : prev.exclVAT;
                                const catalog = parseInputNumber(prev.catalogPrice);
                                const nextDiscount =
                                  catalog !== undefined && catalog > 0 && up !== undefined
                                    ? String(round2(((catalog - up) / catalog) * 100))
                                    : prev.discountPct;
                                return {
                                  ...prev,
                                  unitPrice: value,
                                  discountPct: nextDiscount,
                                  exclVAT: String(excl),
                                };
                              });
                            }}
                          />
                        </View>
                        <View width={{ base: "100%", L: "50%" }}>
                          <TextField
                            label={_t("quantity", "Quantity")}
                            width="100%"
                            value={modalNumericInputs.qty}
                            onChange={(value) => {
                              setModalNumericInputs((prev) => {
                                const qty = parseInputNumber(value);
                                const up = parseInputNumber(prev.unitPrice);
                                const excl = qty !== undefined && up !== undefined ? calcExcl(qty, up) : prev.exclVAT;
                                return { ...prev, qty: value, exclVAT: String(excl) };
                              });
                            }}
                          />
                        </View>
                      </Flex>
                      
                      <Flex direction={{ base: "column", L: "row" }} gap="size-200">
                        <View width={{ base: "100%", L: "50%" }}>
                          <TextField
                            label={_t("vat_percent", "VAT %")}
                            width="100%"
                            value={modalNumericInputs.vatPct}
                            onChange={(value) =>
                              setModalNumericInputs((prev) => ({ ...prev, vatPct: value }))
                            }
                          />
                        </View>
                        <View width={{ base: "100%", L: "50%" }}>
                          <TextField
                            label={_t("total_excl_vat", "Total excl. VAT")}
                            width="100%"
                            value={modalNumericInputs.exclVAT}
                            onChange={(value) => {
                              setModalNumericInputs((prev) => {
                                const excl = parseInputNumber(value);
                                const qty = parseInputNumber(prev.qty);
                                const unitPrice =
                                  excl !== undefined && qty !== undefined && qty !== 0
                                    ? round2(excl / qty)
                                    : parseInputNumber(prev.unitPrice);
                                return {
                                  ...prev,
                                  exclVAT: value,
                                  unitPrice: unitPrice !== undefined ? String(unitPrice) : prev.unitPrice,
                                };
                              });
                            }}
                          />
                        </View>
                      </Flex>

                      <TextArea
                        label={_t("description", "Description")}
                        width="100%"
                        value={modalDraft.description}
                        onChange={(value) => updateModalDraft({ description: value })}
                      />
                    </Flex>
                  </Item>

                  <Item key="advanced">
                    <Flex direction="column" gap="size-200">
                      <Flex direction={{ base: "column", L: "row" }} gap="size-200">
                        <View width={{ base: "100%", L: "50%" }}>
                          <DatePicker
                            label={_t("date", "Date")}
                            width="100%"
                            value={
                              modalDraft.transactionDate
                                ? parseDate(String(modalDraft.transactionDate).slice(0, 10))
                                : undefined
                            }
                            onChange={(value) =>
                              updateModalDraft({
                                transactionDate: value?.toString() || "",
                              })
                            }
                          />
                        </View>
                        <View width={{ base: "100%", L: "50%" }} />
                      </Flex>
                      <Flex direction={{ base: "column", L: "row" }} gap="size-200">
                        <View width={{ base: "100%", L: "50%" }}>
                          <Picker
                            label={_t("transaction_analysis", "Transaction analysis")}
                            width="100%"
                            selectedKey={modalDraft.transactionAnalysis || null}
                            onSelectionChange={(key) =>
                              updateModalDraft({
                                transactionAnalysis: key ? String(key) : "",
                              })
                            }
                            placeholder={_t("select_account", "Select an account...")}
                            items={accountOptions}
                          >
                            {(item) => <Item key={item.id}>{item.label}</Item>}
                          </Picker>
                        </View>
                        <View width={{ base: "100%", L: "50%" }}>
                          <UserDropDown
                            selectedUser={modalDraft.transactionOwnerId}
                            onChange={(data) =>
                              updateModalDraft({
                                transactionOwnerId: data?.id ? String(data.id) : "",
                                transactionOwnerName: data?.name ? String(data.name) : "",
                              })
                            }
                            isEdit={false}
                        label={_t("issued_by", "Issued by")}
                            width={"100%"}
                          />
                        </View>
                      </Flex>
                    </Flex>
                  </Item>

                  <Item key="controlling">
                    <Flex direction="column" gap="size-200">
                      <Flex alignItems="center" justifyContent="space-between" gap="size-150" wrap>
                        <Flex gap="size-100">
                          <Button
                            variant="secondary"
                            UNSAFE_className={styles.shareSecondaryBtn}
                            onPress={recountModalShares}
                          >
                            {_t("recount", "Recount")}
                          </Button>
                          <Button
                            variant="primary"
                            UNSAFE_className={styles.sharePrimaryBtn}
                            onPress={addModalShareRow}
                          >
                            {_tt("button_add", "Add")}
                          </Button>
                          <Button
                            variant="secondary"
                            UNSAFE_className={styles.shareSecondaryBtn}
                            isDisabled={!selectedShareId || (modalDraft?.shares?.length || 0) <= 1}
                            onPress={removeSelectedShareRow}
                          >
                            {_tt("button_remove", "Remove")}
                          </Button>
                        </Flex>
                        <Flex alignItems="center" gap="size-100">
                          <span>{_t("split_transaction_by", "Split transaction by:")}</span>
                          <RadioGroup
                            orientation="horizontal"
                            value={modalDraft.shareMode}
                            onChange={(value) =>
                              updateModalDraft({
                                shareMode: value as TransactionModalDraft["shareMode"],
                              })
                            }
                          >
                            <Radio value="quantity">{_t("quantity", "Quantity")}</Radio>
                            <Radio value="unitsShare">{_t("units_share", "Units Share")}</Radio>
                            <Radio value="percentShare">{_t("percent_share", "% Share")}</Radio>
                            <Radio value="value">{_t("value", "Value")}</Radio>
                          </RadioGroup>
                        </Flex>
                      </Flex>

                      <div className={styles.shareTableWrapper}>
                        <table className={styles.shareTable}>
                          <thead>
                            <tr>
                              <th>{getShareHeaderByMode(modalDraft.shareMode)}</th>
                              <th>%</th>
                              <th className={styles.shareTableCellRight}>
                                {t('total', { ns: 'layout_components', defaultValue: 'Total' })}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {modalDraft.shares.map((share) => {
                              const qty = toNum(parseInputNumber(modalNumericInputs.qty));
                              const unitPrice = toNum(parseInputNumber(modalNumericInputs.unitPrice));
                              const percentValue = sharePercentByMode(
                                share,
                                modalDraft.shareMode,
                                qty,
                                modalExclValue
                              );
                              const totalValue = shareTotalByMode(
                                share,
                                modalDraft.shareMode,
                                qty,
                                unitPrice,
                                modalExclValue
                              );
                              return (
                                <tr
                                  key={share.id}
                                  className={selectedShareId === share.id ? styles.shareRowSelected : ""}
                                  onClick={() => startEditModalShareRow(share.id)}
                                  style={{ cursor: "pointer" }}
                                >
                                  <td>{toNum(share.share).toFixed(2)}</td>
                                  <td>{percentValue.toFixed(2)}%</td>
                                  <td className={styles.shareTableCellRight}>
                                    {formatMoney(
                                      totalValue,
                                      resolveCurrencyLabel(activeModalRow?.data?.currency || invoice?.currency)
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            {shareEditor && (
                              <tr>
                                <td colSpan={3} className={styles.shareEditorCell}>
                                  <Flex gap="size-150" alignItems="end" wrap>
                                    <View flex>
                                      <TextField
                                        aria-label={_t("share_value", "Share value")}
                                        value={shareEditor.value}
                                        onChange={(value) =>
                                          setShareEditor((prev) => (prev ? { ...prev, value } : prev))
                                        }
                                        width="100%"
                                      />
                                    </View>
                                    <Button
                                      variant="secondary"
                                      UNSAFE_className={styles.shareSecondaryBtn}
                                      onPress={cancelShareEditor}
                                    >
                                      {_tt("button_cancel", "Cancel")}
                                    </Button>
                                    <Button
                                      variant="cta"
                                      UNSAFE_className={styles.sharePrimaryBtn}
                                      onPress={saveShareEditor}
                                    >
                                      {shareEditor.mode === "add"
                                        ? _tt("button_add", "Add")
                                        : _tt("button_update", "Update")}
                                    </Button>
                                  </Flex>
                                </td>
                              </tr>
                            )}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td colSpan={2} className={styles.shareFooterLabel}>{_t("total", "Total:")}</td>
                              <td className={styles.shareFooterValue}>
                                {formatMoney(
                                  modalExclValue,
                                  resolveCurrencyLabel(activeModalRow?.data?.currency || invoice?.currency)
                                )}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      <div className={styles.shareTableHint}>
                        {_t("share_sum", "Share sum:")} {modalShareTotal.toFixed(2)}%
                      </div>
                    </Flex>
                  </Item>
                </TabPanels>
              </Tabs>
              </Content>
            </Dialog>
            <Flex
              UNSAFE_style={{
                backgroundColor: "white",
                justifyContent: "end",
                width: "100%",
                padding: "1rem",
                paddingLeft: 55,
                paddingRight: 50,
                boxShadow: "0 -1px 1px rgba(0,0,0,0.1)",
                position: "sticky",
                bottom: 0,
                zIndex: 1,
              }}
            >
              <Flex gap={"size-200"} UNSAFE_className={styles.modalFooterActions}>
                <Button
                  variant="secondary"
                  UNSAFE_className={styles.modalCancelBtn}
                  onPress={handleCancelTransactionModal}
                >
                  {_tt("button_cancel", "Cancel")}
                </Button>
                <Button
                  variant="cta"
                  UNSAFE_className={styles.modalOkBtn}
                  onPress={saveTransactionModal}
                >
                  {_tt("button_ok", "OK")}
                </Button>
              </Flex>
            </Flex>
          </>
        )}
      </DialogContainer>

      <ProductSelectionModal
        isOpen={isMultiAddOpen}
        onClose={() => setIsMultiAddOpen(false)}
        onSave={handleMultiProductSave}
      />
    </Flex>
  );
}
